import { Phone } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const TermsOfUse = () => {
  return (
    <div className="min-h-screen">
      <Header />

      <main className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl font-bold mb-8">Terms of Use</h1>
            <p className="text-muted-foreground mb-8">
              <strong>Last Updated:</strong> January 2025
            </p>

            <div className="prose prose-lg max-w-none space-y-8">
              <section>
                <h2 className="text-3xl font-bold mb-4">Agreement to Terms</h2>
                <p className="text-muted-foreground">
                  By accessing and using the Daniel Albert Law Firm website (danielalbertlaw.com), you accept and agree to be bound by these Terms of Use and our Privacy Policy. If you do not agree to these terms, please do not use this website.
                </p>
              </section>

              <section className="bg-destructive/10 border-l-4 border-destructive p-6 rounded-lg">
                <h2 className="text-2xl font-bold mb-4 text-destructive">Important Legal Disclaimer</h2>
                
                <h3 className="text-xl font-semibold mb-3">No Attorney-Client Relationship</h3>
                <p className="text-muted-foreground mb-4">
                  <strong>Use of this website does not create an attorney-client relationship.</strong> Submitting a contact form, sending an email, or calling our office does not establish an attorney-client relationship. An attorney-client relationship is formed only when we explicitly agree to represent you through a signed engagement agreement.
                </p>

                <h3 className="text-xl font-semibold mb-3">Confidentiality Warning</h3>
                <p className="text-muted-foreground mb-4">
                  <strong>Do not send confidential or time-sensitive information through this website.</strong> Communications via contact forms, email, or other website features are not secure and may not be protected by attorney-client privilege. Until an attorney-client relationship is established, do not share sensitive details about your case.
                </p>

                <h3 className="text-xl font-semibold mb-3">No Legal Advice</h3>
                <p className="text-muted-foreground">
                  <strong>Information on this website is for general informational purposes only and does not constitute legal advice.</strong> The law is complex and constantly changing. Do not rely on website content as a substitute for personalized legal advice from a qualified attorney regarding your specific situation.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Case Results and Testimonials</h2>
                <p className="text-muted-foreground mb-4">
                  Any case results, settlements, or verdicts mentioned on this website are specific to those particular cases and do not guarantee or predict similar outcomes in other cases. Every case is unique based on its specific facts, circumstances, and applicable law.
                </p>
                <p className="text-muted-foreground">
                  Client testimonials reflect the individual experiences of those clients and do not guarantee that all clients will achieve similar results. Past performance does not guarantee future results.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Jurisdiction and Practice Areas</h2>
                <p className="text-muted-foreground mb-4">
                  Daniel Albert Law Firm is licensed to practice law in the State of Texas. Information on this website is based primarily on Texas law unless otherwise specified. Laws vary by jurisdiction, and legal principles discussed may not apply in your state.
                </p>
                <p className="text-muted-foreground">
                  We do not guarantee coverage of all practice areas or services in all locations. Contact us directly to confirm whether we can assist with your specific legal matter.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Intellectual Property</h2>
                <p className="text-muted-foreground">
                  All content on this website, including text, graphics, logos, images, and software, is the property of Daniel Albert Law Firm or its licensors and is protected by U.S. and international copyright laws. You may not reproduce, distribute, modify, or create derivative works from website content without express written permission.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Acceptable Use</h2>
                <p className="text-muted-foreground mb-4">You agree not to:</p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>Use the website for any unlawful purpose or in violation of these Terms</li>
                  <li>Attempt to gain unauthorized access to website systems or networks</li>
                  <li>Interfere with or disrupt website functionality</li>
                  <li>Transmit viruses, malware, or other harmful code</li>
                  <li>Collect user information without authorization</li>
                  <li>Impersonate any person or entity</li>
                  <li>Use automated systems to access the website without permission</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Third-Party Links and Resources</h2>
                <p className="text-muted-foreground">
                  Our website may contain links to third-party websites and resources (such as government websites, legal databases, or other external tools). These links are provided for your convenience only. We do not endorse, control, or assume responsibility for the content, privacy policies, or practices of third-party sites.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Disclaimers and Limitations of Liability</h2>
                
                <h3 className="text-xl font-semibold mb-3">Website Provided "As Is"</h3>
                <p className="text-muted-foreground mb-4">
                  This website is provided "as is" and "as available" without warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
                </p>

                <h3 className="text-xl font-semibold mb-3">No Guarantee of Accuracy</h3>
                <p className="text-muted-foreground mb-4">
                  While we strive to provide accurate and up-to-date information, we make no representations or warranties about the completeness, accuracy, or reliability of website content. Laws change frequently, and information may become outdated.
                </p>

                <h3 className="text-xl font-semibold mb-3">Limitation of Liability</h3>
                <p className="text-muted-foreground">
                  To the fullest extent permitted by law, Daniel Albert Law Firm shall not be liable for any indirect, incidental, consequential, special, or punitive damages arising from your use of or inability to use this website, even if advised of the possibility of such damages.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Indemnification</h2>
                <p className="text-muted-foreground">
                  You agree to indemnify, defend, and hold harmless Daniel Albert Law Firm and its attorneys, employees, and agents from any claims, liabilities, damages, losses, and expenses arising from your use of the website or violation of these Terms.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Modifications to Terms</h2>
                <p className="text-muted-foreground">
                  We reserve the right to modify these Terms of Use at any time without prior notice. Changes become effective immediately upon posting to the website. Your continued use of the website after changes constitutes acceptance of the modified Terms.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Governing Law and Jurisdiction</h2>
                <p className="text-muted-foreground">
                  These Terms are governed by the laws of the State of Texas, without regard to conflict of law principles. Any disputes arising from these Terms or use of this website shall be resolved exclusively in the state or federal courts located in Harris County, Texas.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Severability</h2>
                <p className="text-muted-foreground">
                  If any provision of these Terms is found to be unlawful, void, or unenforceable, that provision shall be deemed severable and shall not affect the validity and enforceability of the remaining provisions.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Contact Information</h2>
                <p className="text-muted-foreground mb-4">
                  Questions about these Terms of Use? Contact us:
                </p>
                <div className="bg-card p-6 rounded-lg">
                  <p className="font-semibold mb-2">Daniel Albert Law Firm</p>
                  <p className="text-muted-foreground">10925 Beechnut St A106</p>
                  <p className="text-muted-foreground">Houston, TX 77072</p>
                  <p className="text-muted-foreground mt-2">
                    <strong>Phone:</strong> (832) 930-3059 (English) | (832) 356-7254 (Español)
                  </p>
                  <p className="text-muted-foreground">
                    <strong>Email:</strong> info@danielalbertlaw.com
                  </p>
                </div>
              </section>

              <section className="bg-accent/10 border-l-4 border-accent p-6 rounded-lg">
                <h2 className="text-2xl font-bold mb-4">Ready to Discuss Your Case?</h2>
                <p className="text-muted-foreground mb-4">
                  If you need legal representation, contact us to schedule a consultation. We'll discuss your situation confidentially and determine how we can help.
                </p>
                <p className="font-semibold flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  Call: (832) 930-3059 (English) | (832) 356-7254 (Español)
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfUse;
